import * as configcat from "configcat-js";
import { useEffect, useState } from "react";
import { DEFAULT_FEATURE_FLAGS, type FeatureFlags, type User } from "./authProvider.types";

let clientInstance: configcat.IConfigCatClient | null = null;

function getClient(sdkKey: string): configcat.IConfigCatClient {
  if (!clientInstance) {
    clientInstance = configcat.getClient(sdkKey, configcat.PollingMode.AutoPoll);
  }
  return clientInstance;
}

interface UseConfigCatFlagsOptions {
  user: User | null | undefined;
  sdkKey: string;
}

export function useConfigCatFlags({
  user,
  sdkKey,
}: UseConfigCatFlagsOptions): FeatureFlags | undefined {
  const [flags, setFlags] = useState<FeatureFlags | undefined>(undefined);

  useEffect(() => {
    if (!user || !sdkKey) {
      setFlags(undefined);
      return;
    }

    const client = getClient(sdkKey);
    const configCatUser = new configcat.User(user.sub, user.mail, undefined, {
      tenantId: user.tenantId,
    });

    let cancelled = false;

    async function fetchFlags() {
      try {
        const [usePresence, useRealTimeAnnotations] = await Promise.all([
          client.getValueAsync("usePresence", false, configCatUser),
          client.getValueAsync("useRealTimeAnnotations", false, configCatUser),
        ]);

        if (!cancelled) {
          setFlags({ usePresence, useRealTimeAnnotations });
        }
      } catch (error) {
        console.error("Failed to fetch feature flags from ConfigCat:", error);
        if (!cancelled) {
          setFlags(DEFAULT_FEATURE_FLAGS);
        }
      }
    }

    fetchFlags();

    return () => {
      cancelled = true;
    };
  }, [user, sdkKey]);

  return flags;
}
